(function () {
  'use strict';

  var root = document.querySelector('.editions-roadshow');
  if (!root) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) root.classList.add('er-reduced-motion');

  var hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  if (hasGsap) window.gsap.registerPlugin(window.ScrollTrigger);

  var lenis = null;

  function initLenis() {
    if (reducedMotion || typeof window.Lenis === 'undefined') return;
    lenis = new window.Lenis({ lerp: 0.12, smoothWheel: true });

    if (hasGsap) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add(function (time) {
        lenis.raf(time * 1000);
      });
      window.gsap.ticker.lagSmoothing(0);
    } else {
      requestAnimationFrame(function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      });
    }
  }

  // Visibility reveals (fade, mask-line, draw-in rules) deliberately do NOT
  // depend on GSAP/ScrollTrigger — only on this native IntersectionObserver,
  // so a CDN hiccup or third-party script failure can never leave page
  // content permanently invisible. GSAP is reserved for effects with safe
  // static fallbacks (parallax, counters, marquee speed).
  function initReveals() {
    var targets = document.querySelectorAll('[data-er-reveal], [data-er-line-reveal], .er-rule');

    if (reducedMotion) {
      targets.forEach(function (el) { el.classList.add('er-in-view'); });
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach(function (el) { el.classList.add('er-in-view'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('er-in-view');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -10% 0px' });

    targets.forEach(function (el) { observer.observe(el); });
  }

  function initMarquees() {
    var marquees = document.querySelectorAll('[data-er-marquee]');

    marquees.forEach(function (marquee) {
      var track = marquee.querySelector('[data-er-marquee-track]');
      if (!track || reducedMotion || !hasGsap) return;

      var direction = marquee.dataset.direction === 'right' ? 1 : -1;
      var baseSpeed = parseFloat(marquee.dataset.speed || '40');
      var totalWidth = track.scrollWidth / 2;

      var tween = window.gsap.to(track, {
        x: direction * -totalWidth,
        duration: totalWidth / (baseSpeed * 4),
        ease: 'none',
        repeat: -1,
        modifiers: {
          x: function (x) {
            var n = parseFloat(x) % totalWidth;
            return (n - totalWidth) + 'px';
          }
        }
      });

      window.ScrollTrigger.create({
        trigger: marquee,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: function () {
          var velocity = lenis ? Math.min(Math.abs(lenis.velocity || 0), 60) : 0;
          tween.timeScale(1 + velocity / 30);
        }
      });
    });
  }

  // Deliberately uses only native rAF + IntersectionObserver, no GSAP — a
  // stat number must never be stuck at its "0" placeholder if a third-party
  // script fails to load.
  function initCounters() {
    var counters = document.querySelectorAll('[data-er-counter]');

    counters.forEach(function (el) {
      var target = parseFloat(el.dataset.erCounter || '0');
      var suffix = el.dataset.erCounterSuffix || '';
      var decimals = el.dataset.erCounterDecimals ? parseInt(el.dataset.erCounterDecimals, 10) : 0;

      function setFinal() {
        el.textContent = target.toFixed(decimals) + suffix;
      }

      if (reducedMotion || typeof IntersectionObserver === 'undefined') {
        setFinal();
        return;
      }

      function animate() {
        var duration = 1400;
        var start = null;

        function step(timestamp) {
          if (start === null) start = timestamp;
          var progress = Math.min((timestamp - start) / duration, 1);
          var eased = 1 - Math.pow(1 - progress, 3);
          el.textContent = (target * eased).toFixed(decimals) + suffix;
          if (progress < 1) requestAnimationFrame(step);
        }

        requestAnimationFrame(step);
      }

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animate();
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.4 });

      observer.observe(el);
    });
  }

  function initParallax() {
    if (!hasGsap || reducedMotion) return;

    var els = document.querySelectorAll('[data-er-parallax]');
    els.forEach(function (el) {
      var amount = parseFloat(el.dataset.erParallax || '40');
      window.gsap.fromTo(el, { yPercent: -amount / 2 }, {
        yPercent: amount / 2,
        ease: 'none',
        scrollTrigger: {
          trigger: el.closest('[data-er-parallax-container]') || el.parentElement,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });
    });
  }

  function initStickyNavIndex() {
    var label = document.querySelector('[data-er-nav-index]');
    var chapters = document.querySelectorAll('[data-er-chapter]');
    if (!label || !chapters.length || !hasGsap) return;

    chapters.forEach(function (chapter) {
      window.ScrollTrigger.create({
        trigger: chapter,
        start: 'top center',
        end: 'bottom center',
        onEnter: function () { label.textContent = chapter.dataset.erChapter; },
        onEnterBack: function () { label.textContent = chapter.dataset.erChapter; }
      });
    });
  }

  function initGalleries() {
    var galleries = document.querySelectorAll('[data-er-gallery]');

    galleries.forEach(function (gallery) {
      var images = gallery.querySelectorAll('.er-gallery-image');
      var dots = gallery.querySelectorAll('.er-gallery-dot');
      var prevButton = gallery.querySelector('[data-gallery-prev]');
      var nextButton = gallery.querySelector('[data-gallery-next]');
      if (images.length < 2) return;

      var current = 0;

      function show(index) {
        current = (index + images.length) % images.length;
        images.forEach(function (img, i) { img.classList.toggle('is-active', i === current); });
        dots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === current); });
      }

      if (prevButton) prevButton.addEventListener('click', function (e) { e.stopPropagation(); show(current - 1); });
      if (nextButton) nextButton.addEventListener('click', function (e) { e.stopPropagation(); show(current + 1); });

      // Drag / swipe support
      var isDown = false;
      var startX = 0;
      var dragged = false;

      function down(e) {
        isDown = true;
        dragged = false;
        gallery.classList.add('er-dragging');
        startX = e.touches ? e.touches[0].clientX : e.clientX;
      }

      function move(e) {
        if (!isDown) return;
        var x = e.touches ? e.touches[0].clientX : e.clientX;
        if (Math.abs(x - startX) > 8) dragged = true;
      }

      function up(e) {
        if (!isDown) return;
        isDown = false;
        gallery.classList.remove('er-dragging');
        var endX = (e.changedTouches ? e.changedTouches[0].clientX : e.clientX);
        var delta = endX - startX;
        if (Math.abs(delta) > 40) {
          delta < 0 ? show(current + 1) : show(current - 1);
        }
      }

      gallery.addEventListener('mousedown', down);
      gallery.addEventListener('mousemove', move);
      gallery.addEventListener('mouseup', up);
      gallery.addEventListener('mouseleave', function () { isDown = false; gallery.classList.remove('er-dragging'); });
      gallery.addEventListener('touchstart', down, { passive: true });
      gallery.addEventListener('touchmove', move, { passive: true });
      gallery.addEventListener('touchend', up);
    });
  }

  function initDragCarousels() {
    var carousels = document.querySelectorAll('[data-er-drag]');

    carousels.forEach(function (carousel) {
      var isDown = false;
      var startX = 0;
      var scrollLeft = 0;

      function down(e) {
        isDown = true;
        carousel.classList.add('er-dragging');
        startX = e.touches ? e.touches[0].pageX : e.pageX;
        scrollLeft = carousel.scrollLeft;
      }

      function move(e) {
        if (!isDown) return;
        var x = e.touches ? e.touches[0].pageX : e.pageX;
        if (!e.touches) e.preventDefault();
        carousel.scrollLeft = scrollLeft - (x - startX);
      }

      function up() {
        isDown = false;
        carousel.classList.remove('er-dragging');
      }

      carousel.addEventListener('mousedown', down);
      carousel.addEventListener('mousemove', move);
      carousel.addEventListener('mouseleave', up);
      carousel.addEventListener('mouseup', up);
      carousel.addEventListener('touchstart', down, { passive: true });
      carousel.addEventListener('touchmove', move, { passive: true });
      carousel.addEventListener('touchend', up);
    });
  }

  // Smoothly scrolls in-page anchor links (e.g. every "Register" button
  // points to #register) via Lenis when available, falling back to the
  // browser's native anchor jump otherwise — never silently does nothing.
  function initAnchorScroll() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[href^="#"]');
      if (!link) return;
      // Links that open the registration pop-up handle their own click; never
      // smooth-scroll the page underneath them.
      if (link.hasAttribute('data-er-open-register')) return;
      var id = link.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;

      if (lenis) {
        event.preventDefault();
        lenis.scrollTo(target);
      }
    });
  }

  function initStickyNavVisibility() {
    var nav = document.querySelector('[data-er-sticky-nav]');
    var hero = document.querySelector('[data-er-hero]');
    if (!nav || !hero) return;

    function update() {
      var heroBottom = hero.getBoundingClientRect().bottom;
      nav.classList.toggle('er-sticky-nav--visible', heroBottom < 0);
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  function initAccordions() {
    var items = document.querySelectorAll('[data-er-accordion]');

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-er-accordion-trigger]');
      var panel = item.querySelector('[data-er-accordion-panel]');
      if (!trigger || !panel) return;

      panel.style.height = '0px';
      panel.style.overflow = 'hidden';

      trigger.addEventListener('click', function () {
        var isOpen = item.classList.contains('er-accordion--open');
        var allItems = item.parentElement.querySelectorAll('[data-er-accordion]');

        allItems.forEach(function (other) {
          if (other === item) return;
          var otherPanel = other.querySelector('[data-er-accordion-panel]');
          var otherTrigger = other.querySelector('[data-er-accordion-trigger]');
          other.classList.remove('er-accordion--open');
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
          if (otherPanel && hasGsap && !reducedMotion) {
            window.gsap.to(otherPanel, { height: 0, duration: 0.4, ease: 'power2.inOut' });
          } else if (otherPanel) {
            otherPanel.style.height = '0px';
          }
        });

        if (isOpen) {
          item.classList.remove('er-accordion--open');
          trigger.setAttribute('aria-expanded', 'false');
          if (hasGsap && !reducedMotion) {
            window.gsap.to(panel, { height: 0, duration: 0.4, ease: 'power2.inOut' });
          } else {
            panel.style.height = '0px';
          }
        } else {
          item.classList.add('er-accordion--open');
          trigger.setAttribute('aria-expanded', 'true');
          var target = panel.scrollHeight;
          if (hasGsap && !reducedMotion) {
            window.gsap.to(panel, { height: target, duration: 0.5, ease: 'power2.inOut' });
          } else {
            panel.style.height = target + 'px';
          }
        }
      });
    });
  }

  function init() {
    initLenis();
    initAnchorScroll();
    initStickyNavVisibility();
    initReveals();
    initMarquees();
    initCounters();
    initParallax();
    initStickyNavIndex();
    initDragCarousels();
    initGalleries();
    initAccordions();

    if (hasGsap) window.ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
